import React from 'react';
/* eslint-disable react/function-component-definition */
import { useAuth0 } from '@auth0/auth0-react';
import {
  DefaultButton,
  FontIcon,
  FontWeights,
  mergeStyleSets,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { useAppSelector } from '../redux/hooks';
import { getPermissions, getPermissionsLoaded } from '../redux/permission/permissionSlice';
import theme from '../theme';

interface IPrivateRoute {
  permission?: string;
}

const LOGO_URL = 'https://3bouw.be/wp-content/uploads/2025/01/3bouw-logo-baseline-kleur@3x.webp';
const HERO_URL = 'https://3bouw.be/wp-content/uploads/2025/02/3bouw-houtskeletbouw-inspiratieboek-00-min-scaled.webp';
const FONT = '"Segoe UI Variable", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif';
const MOBILE = '@media (max-width: 860px)';

const classes = mergeStyleSets({
  page: {
    position: 'fixed',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    backgroundColor: theme.palette.white,
    fontFamily: FONT,
  },
  panel: {
    flex: '0 0 44%',
    minWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    padding: '96px 64px 32px',
    boxSizing: 'border-box',
    selectors: {
      [MOBILE]: { flex: 1, minWidth: 0, padding: '80px 24px 24px' },
    },
  },
  form: {
    flex: 1,
    width: '100%',
    maxWidth: 400,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  logo: {
    display: 'block',
    height: 64,
    width: 'auto',
    alignSelf: 'flex-start',
    marginBottom: 64,
  },
  eyebrow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    fontSize: 13,
    fontWeight: FontWeights.semibold,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.themeDark,
  },
  eyebrowWarning: {
    color: '#c50f1f',
  },
  title: {
    margin: '0 0 12px',
    fontSize: 36,
    lineHeight: '44px',
    fontWeight: FontWeights.semibold,
    color: '#242424',
  },
  text: {
    margin: '0 0 40px',
    fontSize: 16,
    lineHeight: '24px',
    color: '#616161',
  },
  button: {
    width: '100%',
    height: 48,
    borderRadius: 8,
    fontSize: 16,
  },
  footer: {
    maxWidth: 400,
    width: '100%',
    margin: '0 auto',
    fontSize: 12,
    color: '#8a8886',
  },
  hero: {
    flex: 1,
    background: `linear-gradient(90deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0) 12%),
      url(${HERO_URL}) center / cover no-repeat`,
    selectors: {
      [MOBILE]: { display: 'none' },
    },
  },
  loading: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT,
  },
});

const checkPermissions = (permissions: string[], permission?: string) => {
  if (!permission) return true;
  return permissions.includes(permission);
};

const AuthLayout: React.FC<{
  icon: string;
  eyebrow: string;
  variant?: 'info' | 'warning';
  title: string;
  text: string;
}> = ({
  icon, eyebrow, variant = 'info', title, text, children,
}) => (
  <div className={classes.page}>
    <main className={classes.panel}>
      <div className={classes.form}>
        <img src={LOGO_URL} alt='3bouw' className={classes.logo} />
        <div className={`${classes.eyebrow} ${variant === 'warning' ? classes.eyebrowWarning : ''}`}>
          <FontIcon iconName={icon} />
          {eyebrow}
        </div>
        <h1 className={classes.title}>{title}</h1>
        <p className={classes.text}>{text}</p>
        {children}
      </div>
      <div className={classes.footer}>
        ©
        {' '}
        {new Date().getFullYear()}
        {' '}
        3bouw · bewust beter bouwen
      </div>
    </main>
    <div className={classes.hero} role='presentation' />
  </div>
);

const PrivateRoute: React.FC<IPrivateRoute> = ({ permission, children }) => {
  const permissions = useAppSelector(getPermissions);
  const permissionsLoaded = useAppSelector(getPermissionsLoaded);
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();

  // ook wachten op de rechten: anders toont een herladen pagina kort "Geen toegang"
  if (isLoading || (isAuthenticated && !permissionsLoaded)) {
    return (
      <div className={classes.loading}>
        <Spinner size={SpinnerSize.large} label='Laden...' />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthLayout
        icon='Contact'
        eyebrow='Bouw Manager'
        title='Aanmelden'
        text='Meld je aan om gebruik te maken van de applicatie.'
      >
        <PrimaryButton
          className={classes.button}
          onClick={() => { loginWithRedirect(); }}
        >
          Meld je aan
        </PrimaryButton>
      </AuthLayout>
    );
  }

  if (!checkPermissions(permissions, permission)) {
    return (
      <AuthLayout
        icon='Lock'
        eyebrow='Beveiligde pagina'
        variant='warning'
        title='Geen toegang'
        text='Je hebt geen rechten om deze pagina te bekijken. Neem contact op met een beheerder als je denkt dat dit niet klopt.'
      >
        <DefaultButton
          className={classes.button}
          iconProps={{ iconName: 'Back' }}
          onClick={() => { window.history.back(); }}
        >
          Ga terug
        </DefaultButton>
      </AuthLayout>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default PrivateRoute;
